/**
 * Turning a Check into real dice. Pure: no OBR, no React, so it is tested directly.
 *
 * How a Dice+ roll stays faithful to the rules engine:
 *  1. Dry run: the engine resolves the Check once with a RECORDING roller, which
 *     tells us exactly which dice it needs (1d20, or 2d20 with a mode) and the
 *     modifier total.
 *  2. Those dice are requested from Dice+, visibly, for the whole table, with a
 *     notation whose total matches the engine's (`2d20kh1+5 #Perception`).
 *  3. Dice+ returns every die face. The engine resolves the Check again with a
 *     roller that replays exactly those faces, so criticals, the kept die and
 *     every consequence come from the rules, never from Dice+.
 * When Dice+ is not in the room, the sheet rolls its own dice instead.
 */
import type { Roller } from '@dcc/rules/rules/dice.ts';
import { randomRoller, sequenceRoller } from '@dcc/rules/rules/dice.ts';
import type { CheckResult } from '@dcc/rules/rules/checks.ts';
import type { DicePlusGroup, ExternalRollOutcome } from '../obr/dicePlus.ts';

export type DiceSource = 'dice-plus' | 'sheet';

export type ObtainedDice =
  | { ok: true; roller: Roller; source: DiceSource; diceTotal: number | null; note: string | null }
  | { ok: false; reason: string };

/** Dice+ request function, or null when Dice+ is not in the room. */
export type DicePlusRequester = ((notation: string) => Promise<ExternalRollOutcome>) | null;

/** A roller that returns a middling face and records every die size asked for. */
export function recordingRoller(): { roller: Roller; sides: number[] } {
  const sides: number[] = [];
  return {
    sides,
    roller: {
      roll(size: number): number {
        sides.push(size);
        return Math.ceil(size / 2);
      },
    },
  };
}

/** Dice+ reads + - * / , and ) as notation, and # and {} as markers, so none may appear in a label. */
export function notationLabel(text: string): string {
  return text.replace(/[+\-*/,(){}#]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
}

function modifierText(modifier: number): string {
  if (modifier === 0) return '';
  return modifier > 0 ? `+${modifier}` : `-${Math.abs(modifier)}`;
}

/** Notation whose Dice+ total equals the engine's total for this Check. */
export function checkNotation(result: CheckResult, label: string): string {
  const count = result.die.dice.length;
  const keep = count === 1 ? '' : result.die.mode === 'advantage' ? 'kh1' : 'kl1';
  const modifier = result.components.reduce((sum, c) => sum + c.value, 0);
  const tail = notationLabel(label);
  return `${count}d20${keep}${modifierText(modifier)}${tail ? ` #${tail}` : ''}`;
}

/** Every die face from a Dice+ result, in order, checked against the sizes the engine asked for. */
export function facesFromDicePlus(groups: readonly DicePlusGroup[], sides: readonly number[]): number[] | string {
  const faces: number[] = [];
  const sizes: number[] = [];
  for (const group of groups) {
    if (group.condensed) return 'Dice+ sent a condensed result without individual dice.';
    const size = Number(group.diceType.replace(/^d/i, ''));
    for (const value of group.values) {
      faces.push(value);
      sizes.push(size);
    }
  }
  if (faces.length !== sides.length) {
    return `Dice+ returned ${faces.length} dice; the rules needed ${sides.length}.`;
  }
  for (let i = 0; i < sides.length; i += 1) {
    const size = sides[i] as number;
    const face = faces[i] as number;
    if (sizes[i] !== size) return `Dice+ rolled a d${sizes[i]} where the rules needed a d${size}.`;
    if (!Number.isInteger(face) || face < 1 || face > size) return `Dice+ returned an impossible d${size} face (${face}).`;
  }
  return faces;
}

/**
 * Gets the dice for one Check.
 *
 * `dryRun` resolves the Check with the roller it is given and returns the
 * result, or null when the Check cannot be rolled (the real run will report why).
 */
export async function obtainCheckDice(
  dryRun: (roller: Roller) => CheckResult | null,
  label: string,
  requestDicePlus: DicePlusRequester,
): Promise<ObtainedDice> {
  if (!requestDicePlus) {
    return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: 'Dice+ is not in this room, so the sheet rolled.' };
  }
  const recording = recordingRoller();
  const dry = dryRun(recording.roller);
  if (!dry) return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: null };

  const outcome = await requestDicePlus(checkNotation(dry, label));
  if (!outcome.ok) return { ok: false, reason: `Dice+ could not roll: ${outcome.error} Nothing was recorded.` };

  const faces = facesFromDicePlus(outcome.groups, recording.sides);
  if (typeof faces === 'string') return { ok: false, reason: `${faces} Nothing was recorded.` };
  return { ok: true, roller: sequenceRoller(faces), source: 'dice-plus', diceTotal: outcome.total, note: null };
}

/** A note when Dice+ displayed a different total from the rules engine. Null when they agree. */
export function totalMismatchNote(diceTotal: number | null, engineTotal: number): string | null {
  if (diceTotal === null || diceTotal === engineTotal) return null;
  return `Dice+ showed ${diceTotal}; the rules total is ${engineTotal}. The sheet recorded ${engineTotal}.`;
}

/** Dice+ notation for a roll that simply adds dice and flat numbers (damage). */
export function additiveNotation(sides: readonly number[], flat: number, label: string): string {
  const runs: { count: number; size: number }[] = [];
  for (const size of sides) {
    const last = runs[runs.length - 1];
    if (last && last.size === size) last.count += 1;
    else runs.push({ count: 1, size });
  }
  const dice = runs.map((r) => `${r.count}d${r.size}`).join('+');
  const tail = notationLabel(label);
  const body = dice ? `${dice}${modifierText(flat)}` : String(flat);
  return `${body}${tail ? ` #${tail}` : ''}`;
}

/**
 * Gets the dice for an additive roll (damage). `dryRun` performs the roll with
 * the roller it is given and returns its total, or null when it cannot roll.
 */
export async function obtainAdditiveDice(
  dryRun: (roller: Roller) => number | null,
  label: string,
  requestDicePlus: DicePlusRequester,
): Promise<ObtainedDice> {
  if (!requestDicePlus) {
    return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: 'Dice+ is not in this room, so the sheet rolled.' };
  }
  const recording = recordingRoller();
  const dryTotal = dryRun(recording.roller);
  if (dryTotal === null) return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: null };
  if (recording.sides.length === 0) return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: null };

  const dryFaces = recording.sides.map((size) => Math.ceil(size / 2));
  const flat = dryTotal - dryFaces.reduce((sum, face) => sum + face, 0);
  const outcome = await requestDicePlus(additiveNotation(recording.sides, flat, label));
  if (!outcome.ok) return { ok: false, reason: `Dice+ could not roll: ${outcome.error} Nothing was recorded.` };

  const faces = facesFromDicePlus(outcome.groups, recording.sides);
  if (typeof faces === 'string') return { ok: false, reason: `${faces} Nothing was recorded.` };
  return { ok: true, roller: sequenceRoller(faces), source: 'dice-plus', diceTotal: outcome.total, note: null };
}

/** Dice for a roll whose notation and die sizes are already known (an AI Favor reroll). */
export async function obtainNotationDice(
  notation: string,
  sides: readonly number[],
  requestDicePlus: DicePlusRequester,
): Promise<ObtainedDice> {
  if (!requestDicePlus) {
    return { ok: true, roller: randomRoller, source: 'sheet', diceTotal: null, note: 'Dice+ is not in this room, so the sheet rolled.' };
  }
  const outcome = await requestDicePlus(notation);
  if (!outcome.ok) return { ok: false, reason: `Dice+ could not roll: ${outcome.error} Nothing was recorded.` };
  const faces = facesFromDicePlus(outcome.groups, sides);
  if (typeof faces === 'string') return { ok: false, reason: `${faces} Nothing was recorded.` };
  return { ok: true, roller: sequenceRoller(faces), source: 'dice-plus', diceTotal: outcome.total, note: null };
}

